<template src="./template.html"></template>
<style src="./styles.scss" lang="scss" scoped></style>

<script>
  import { fixEditorContentForLineClamp, getSocialMediaIconForSource } from '@/helper'
  import { mapActions, mapState } from 'vuex'
  import { some } from 'lodash'
  import api from '@/api'
  import { isMobile } from 'mobile-device-detect'
  import AttachmentItem from './AttachmentItem'
  import LinkPreviewItem from './LinkPreviewItem'
  import EditPostOverlay from '@/components/overlays/EditPostOverlay'
  import FallbackAvatar from '@/components/ui/FallbackAvatar'
  import EventPreview from '@/components/layout/EventPreview'
  import ConfirmationOverlay from '@/components/overlays/ConfirmationOverlay'
  import OverlayGallery from '@/components/overlays/OverlayGallery'

  export default {
    name: 'NewsItem',
    components: {
      AttachmentItem,
      LinkPreviewItem,
      EditPostOverlay,
      ConfirmationOverlay,
      CTA: () => import('@/components/modules/CTA'),
      OverlayGallery,
      EventPreview,
      FallbackAvatar,
    },
    props: {
      news: { type: Object, default: null },
      showActionItems: Boolean,
      groupId: { type: Number, default: null },
      topicImg: String,
      topicId: { type: Number, default: null },
      hash: String,
    },
    data() {
      return {
        overlayIndex: null,
        attachments: [],
        selectedOverlay: null,
        breadcrumbItems: [],
        clampLines: 0,
        isClamped: false,
        initialClamped: false,
        comments: {
          isPending: false,
          data: {},
        },
        likes: {
          isPending: false,
          data: {},
        },
        statusMessage: null,
        isStatusVisible: false,
      }
    },
    computed: {
      ...mapState('auth', ['settings']),
      canDelete() { return this.news && some(this.news.permissions, p => p === 'delete') },
      canEdit() { return this.news && some(this.news.permissions, p => p === 'edit') },
      getAttachmentClass() {
        return this.news.meta.type !== 'post' ? 'order-first' : 'order-last'
      },
      getContentClass() {
        let contentClass = this.attachments.length ? 'col-sm-7 pr-sm-0 has-attachments' : 'pr-sm-12'
        contentClass += this.news.meta.type !== 'post' ? ' order-last' : ' order-first'
        return contentClass
      },
      getLineClampLines() {
        if (this.isClamped) {
          return 0
        } else if (this.exceptionalCustomers()) {
          const base_lines = this.attachments.length ? 9 : 7
          if (this.news.meta.type === 'article') {
            return 9
          }
          return this.showTitle ? base_lines - 4 : base_lines
        } else {
          return isMobile ? 7 : 4
        }
      },
      getDeletePostObj() {
        const delete_post_object = this.$t('news').news_item.overlays.delete_post
        delete_post_object['img'] = delete_post_object['img'] || this.topicImg
        return delete_post_object
      },
      showTitle() {
        const { title, teaser } = this.news
        return title && teaser.toLowerCase().indexOf(title.toLowerCase().slice(0, -4)) === -1
      },
      toggleText() {
        return this.isClamped ? this.$t('toggle_show.expand') : this.$t('toggle_show.collapse')
      },
    },
    created() {
      const meta = this.news.meta
      const breadcrumbItems = []
      if (!this.groupId && meta.group && meta.type !== 'article') {
        breadcrumbItems.push({
          exact: true,
          text: meta.group.name,
          to: {
            name: 'group',
            params: { id: meta.group.id },
          },
          disabled: false,
        })
      }
      if (!this.topicId && meta.topic) {
        if (meta.group && meta.type !== 'article') {
          breadcrumbItems.push({
            exact: true,
            text: meta.topic.name,
            to: {
              name: 'topic',
              params: { group_id: meta.group.id, id: meta.topic.id },
            },
            disabled: false,
          })
        } else {
          breadcrumbItems.push({
            exact: true,
            text: meta.topic.name,
            to: {
              name: 'category',
              params: { id: meta.topic.id },
            },
            disabled: false,
          })
        }
      }
      this.breadcrumbItems = breadcrumbItems

      const news = this.news
      this.attachments = news.images && news.images.teaser ? [ news.images.teaser ] : news.attachments

      if (!Object.prototype.hasOwnProperty.call(news, 'likes_allowed') || news.likes_allowed) {
        this.fetchLikes()
      }
      if (news.comments_allowed) {
        this.fetchComments()
      }
      this.clampLines = this.getLineClampLines
    },
    mounted() {
      const el = this.$refs.post_text
      if (el) {
        this.isClamped = this.initialClamped = el.scrollHeight > el.clientHeight
      }
    },
    methods: {
      ...mapActions('posts', [
        'deletePost',
        'deleteComment',
      ]),
      toggleState(state) {
        state = !state
      },
      closeOverlay() {
        this.selectedOverlay = null
      },
      async fetchComments() {
        this.toggleState(this.comments.isPending)
        api.items.getItems('comments', { type: 'news', id: this.news.id }).then(({ data }) => {
          this.comments.data = data
          this.toggleState(this.comments.isPending)
        })
      },
      async fetchLikes() {
        this.toggleState(this.likes.isPending)
        api.items.getItems('likes', { type: 'news', id: this.news.id }).then(({ data }) => {
          this.likes.data = data
          this.toggleState(this.likes.isPending)
        })
      },
      onLike(val) {
        this.toggleState(this.likes.isPending)
        api.items.editItem('likes', { type: 'news', id: this.news.id, like: val }).then(({ data }) => {
          this.likes.data = data
          this.toggleState(this.likes.isPending)
        })
      },
      onComment(val) {
        this.toggleState(this.comments.isPending)
        api.items.editItem('comments', { type: 'news', id: this.news.id, like: val }).then(({ data }) => {
          this.comments.data = data
          this.toggleState(this.comments.isPending)
        })
      },
      fixEditorContentForLineClamp,
      getSocialMediaIconForSource,
      onDeletePost() {
        this.deletePost(this.news.id)
        .then(_ => {
          this.statusMessage = this.$t(this.successStatusTranslation('post', 'delete'))
          this.isStatusVisible = true
          this.closeOverlay()
        })
      },
      toggleExpand() {
        this.clampLines = this.getLineClampLines
        this.isClamped = !this.isClamped
      },
      onClickAttachmentItem(index) {
        this.overlayIndex = index
      },
      onDeleteComment(selectedCommentId, postId) {
        this.deleteComment(selectedCommentId).then(_ => {
          api.items.getItems('comments', { type: 'news', id: postId })
          .then(({ data }) => {
            this.statusMessage = this.$t(this.successStatusTranslation('cta_comment', 'delete'))
            this.isStatusVisible = true
            this.comments.data = data
          })
        })
      },
    },
  }
</script>
